// app/ClientApp.tsx
"use client";
import dynamic from "next/dynamic";
import DotLoader from "react-lib/apps/IsHealth/Common/DotLoader";

import "../css/Default.css";
import "react-lib/apps/IsHealth/IHMobile/ishealth.scss";
import "react-lib/apps/MeHealthLineLiff/MeHealthHome.scss";
import "react-lib/apps/Innobic/ShopHome.scss";

function AppLoading({ showDots = true }: Readonly<{ showDots?: boolean }>) {
  return (
    <div
      role="status"
      aria-label="กำลังโหลด"
      style={{
        position: "fixed",
        inset: 0,
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        backgroundColor: "rgba(0, 0, 0, 0.85)",
        zIndex: 1000,
      }}
    >
      {showDots && <DotLoader />}
    </div>
  );
}

const App = dynamic(() => import("./App"), {
  ssr: false,
  loading: () => <AppLoading />,
});

const ThemedApp = dynamic(
  async () => {
    if (typeof window !== "undefined") {
      const { default: CONFIG } = await import("config/config");
      const queryParams = new URLSearchParams(window.location.search);
      const nestedParams = new URLSearchParams(
        queryParams.get("line.state") || queryParams.get("liff.state") || "",
      );
      const oa = queryParams.get("oa") || nestedParams.get("oa");
      const dataTheme = oa ? CONFIG.TEMP_THEME_COLOR_PER_OA[oa]?.dataTheme : undefined;

      if (typeof dataTheme === "string" && dataTheme) {
        document.documentElement.setAttribute("data-theme", dataTheme);
      }

      if (queryParams.has("liff.state")) {
        // LINE opens the LIFF endpoint with the real path and query in liff.state,
        // and liff.init() reloads the page at that URL. Loading the app bundle before
        // that reload is wasted, so run it first. On redirect the promise never settles
        // and this loading screen stays up until the page reloads.
        try {
          const { initLiffOnce } = await import("react-lib/apps/common/LiffInit");
          await initLiffOnce();
        } catch (error) {
          console.error("Early LIFF initialization failed:", error);
        }
      } else if (/^\/liff(-pgh|-ramasri)?(\/|$)/.test(window.location.pathname)) {
        // Start liff.init() while the app bundle downloads; LineLiff awaits the same promise.
        import("react-lib/apps/common/LiffInit")
          .then(({ initLiffOnce }) => initLiffOnce())
          .catch((error) => {
            console.error("Early LIFF initialization failed:", error);
          });
      }
    }

    return function ThemedClientApp() {
      return <App />;
    };
  },
  {
    ssr: false,
    loading: () => <AppLoading showDots={false} />,
  },
);

export default function ClientApp() {
  return <ThemedApp />;
}
